import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Users,
  Plus,
  Trash2,
  Save,
  X,
  AlertCircle,
  CheckCircle,
  User,
  Building2,
  Mail,
  Phone,
  Wallet,
  DollarSign,
  ArrowLeft,
  Clock
} from 'lucide-react';
import { formatCurrency, formatDate } from '../../services/api';
import { confirmToast } from '../../utils/sonnerDialogs';

const BidderManagement = () => {
  const navigate = useNavigate();
  const { subscriptionId } = useParams();
  const [showAddForm, setShowAddForm] = useState(false);
  const [bidders, setBidders] = useState([]);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm();

  // Mock subscription details
  useEffect(() => {
    // In real app, fetch from API based on subscriptionId
    const mockDetails = {
      id: subscriptionId,
      bondName: 'Malaysia Infrastructure Bond 2024',
      isin: 'MY0123456789',
      issuer: 'Malaysia Infrastructure Corp',
      subscriptionStartDate: '2024-12-01T09:00:00Z',
      subscriptionEndDate: '2024-12-15T17:00:00Z',
      status: 'Active',
      totalNominalValue: 500000000,
      minSubscription: 100000,
      bondType: 'Conventional'
    };
    setSubscriptionDetails(mockDetails);

    // Mock existing bidders
    const mockBidders = [
      {
        id: 1,
        bidderName: 'Ahmad Bin Abdullah',
        bidderType: 'Individual',
        kycStatus: 'Verified',
        walletAddress: '0x1234567890abcdef1234567890abcdef12345678',
        bidAmount: 250000,
        bidPrice: 100.5,
        contactEmail: 'ahmad.abdullah@email.com',
        contactPhone: '+60123456789',
        additionalNotes: 'High net worth individual, existing client',
        dateAdded: '2024-12-10T10:30:00Z'
      },
      {
        id: 2,
        bidderName: 'Malaysian Pension Fund Berhad',
        bidderType: 'Institutional Investor',
        kycStatus: 'Verified',
        walletAddress: '0xabcdef1234567890abcdef1234567890abcdef12',
        bidAmount: 5000000,
        bidPrice: 99.8,
        contactEmail: 'investments@mpfb.com.my',
        contactPhone: '+603-21234567',
        additionalNotes: 'Large institutional investor, long-term holder',
        dateAdded: '2024-12-09T14:15:00Z'
      }
    ];
    setBidders(mockBidders);
  }, [subscriptionId]);

  const bidderTypes = [
    { value: 'Individual', label: 'Individual' },
    { value: 'Institutional Investor', label: 'Institutional Investor' }
  ];

  const kycStatuses = [
    { value: 'Verified', label: 'Verified', color: 'green' },
    { value: 'Pending', label: 'Pending', color: 'yellow' },
    { value: 'Rejected', label: 'Rejected', color: 'red' }
  ];

  // Form validation rules
  const validationRules = {
    bidderName: {
      required: 'Bidder name is required',
      maxLength: { value: 255, message: 'Name must not exceed 255 characters' },
      pattern: {
        value: /^[a-zA-Z0-9\s.,'-]+$/,
        message: 'Only alphanumeric characters, spaces, and common punctuation allowed'
      }
    },
    bidderType: {
      required: 'Bidder type is required'
    },
    kycStatus: {
      required: 'Verification status is required'
    },
    walletAddress: {
      required: 'Wallet address is required',
      pattern: {
        value: /^0x[a-fA-F0-9]{40}$/,
        message: 'Must be a valid Ethereum address (42 characters, starting with 0x)'
      }
    },
    bidAmount: {
      required: 'Bid amount is required',
      min: { value: 0.01, message: 'Bid amount must be greater than 0' },
      validate: value => {
        if (subscriptionDetails && value < subscriptionDetails.minSubscription) {
          return `Minimum subscription is ${formatCurrency(subscriptionDetails.minSubscription)}`;
        }
        return true;
      }
    },
    bidPrice: {
      required: 'Bid price is required',
      min: { value: 0.01, message: 'Bid price must be greater than 0' }
    },
    contactEmail: {
      required: 'Contact email is required',
      maxLength: { value: 255, message: 'Email must not exceed 255 characters' },
      pattern: {
        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        message: 'Must be a valid email address'
      }
    },
    contactPhone: {
      maxLength: { value: 20, message: 'Phone number must not exceed 20 characters' },
      pattern: {
        value: /^[+\d\s-()]+$/,
        message: 'Phone number can only contain digits, spaces, hyphens, plus signs, and parentheses'
      }
    },
    additionalNotes: {
      maxLength: { value: 1000, message: 'Notes must not exceed 1000 characters' }
    }
  };

  const onSubmit = (data) => {
    // Check for duplicate wallet address
    const isDuplicate = bidders.some(bidder => 
      bidder.walletAddress.toLowerCase() === data.walletAddress.toLowerCase()
    );
    
    if (isDuplicate) {
      alert('This wallet address has already been used for this bond subscription');
      return;
    }

    // Check verification status
    if (data.kycStatus !== 'Verified') {
      alert('Only bidders with verified status can be submitted to blockchain');
      return;
    }

    // Add new bidder
    const newBidder = {
      id: Date.now(),
      ...data,
      bidAmount: parseFloat(data.bidAmount),
      bidPrice: parseFloat(data.bidPrice),
      dateAdded: new Date().toISOString()
    };

    setBidders([...bidders, newBidder]);
    setShowAddForm(false);
    reset();
    
    // Show success message
    alert('Bidder information added successfully');
  };

  const removeBidder = async (bidderId) => {
    const confirmed = await confirmToast('Are you sure you want to remove this bidder?', {
      intent: 'danger',
      confirmLabel: 'Remove bidder'
    });
    if (confirmed) {
      setBidders(bidders.filter(bidder => bidder.id !== bidderId));
    }
  };

  const getKycStatusBadge = (status) => {
    const config = kycStatuses.find(s => s.value === status);
    const colorClasses = {
      green: 'bg-green-100 text-green-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      red: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClasses[config?.color || 'green']}`}>
        {status === 'Verified' && <CheckCircle className="w-3 h-3 mr-1" />}
        {status === 'Pending' && <Clock className="w-3 h-3 mr-1" />}
        {status === 'Rejected' && <X className="w-3 h-3 mr-1" />}
        {status}
      </span>
    );
  };

  const handleReviewBidders = () => {
    if (bidders.length === 0) {
      alert('Please add at least one bidder before proceeding to review');
      return;
    }
    
    const verifiedBidders = bidders.filter(b => b.kycStatus === 'Verified');
    if (verifiedBidders.length === 0) {
      alert('No verified bidders found. Only verified bidders can be submitted to blockchain');
      return;
    }
    
    navigate(`/facility-agent/subscription/${subscriptionId}/review`);
  };

  if (!subscriptionDetails) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Manage Off-Chain Bidders</h1>
              <p className="mt-2 text-sm text-gray-600">
                {subscriptionDetails.bondName} - ISIN: {subscriptionDetails.isin} - Min: {formatCurrency(subscriptionDetails.minSubscription)}
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowAddForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Bidder
              </button>
              <button
                onClick={handleReviewBidders}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Review & Submit
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Add Bidder Form Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-medium text-gray-900">Add Off-Chain Bidder</h3>
                  <button
                    onClick={() => setShowAddForm(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Bidder Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Bidder Name <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                          type="text"
                          {...register('bidderName', validationRules.bidderName)}
                          className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Full legal name"
                        />
                      </div>
                      {errors.bidderName && (
                        <p className="mt-1 text-sm text-red-600">{errors.bidderName.message}</p>
                      )}
                    </div>

                    {/* Bidder Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Bidder Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...register('bidderType', validationRules.bidderType)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="">Select bidder type</option>
                        {bidderTypes.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                      {errors.bidderType && (
                        <p className="mt-1 text-sm text-red-600">{errors.bidderType.message}</p>
                      )}
                    </div>

                    {/* Verification Status */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Verification Status <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...register('kycStatus', validationRules.kycStatus)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="">Select status</option>
                        {kycStatuses.map(status => (
                          <option key={status.value} value={status.value}>{status.label}</option>
                        ))}
                      </select>
                      {errors.kycStatus && (
                        <p className="mt-1 text-sm text-red-600">{errors.kycStatus.message}</p>
                      )}
                      {watch('kycStatus') && watch('kycStatus') !== 'Verified' && (
                        <p className="mt-1 text-sm text-amber-600 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          Only verified bidders can be submitted to blockchain
                        </p>
                      )}
                    </div>

                    {/* Wallet Address */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Wallet Address <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Wallet className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                          type="text"
                          {...register('walletAddress', validationRules.walletAddress)}
                          className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                          placeholder="0x..."
                        />
                      </div>
                      {errors.walletAddress && (
                        <p className="mt-1 text-sm text-red-600">{errors.walletAddress.message}</p>
                      )}
                    </div>

                    {/* Bid Amount */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Bid Amount (Nominal Value) <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                          type="number"
                          step="0.01"
                          {...register('bidAmount', validationRules.bidAmount)}
                          className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="0.00"
                        />
                      </div>
                      {errors.bidAmount && (
                        <p className="mt-1 text-sm text-red-600">{errors.bidAmount.message}</p>
                      )}
                    </div>

                    {/* Bid Price */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Bid Price <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                          type="number"
                          step="0.0001"
                          {...register('bidPrice', validationRules.bidPrice)}
                          className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="100.0000"
                        />
                      </div>
                      {errors.bidPrice && (
                        <p className="mt-1 text-sm text-red-600">{errors.bidPrice.message}</p>
                      )}
                    </div>

                    {/* Contact Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Email <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                          type="email"
                          {...register('contactEmail', validationRules.contactEmail)}
                          className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="email@example.com"
                        />
                      </div>
                      {errors.contactEmail && (
                        <p className="mt-1 text-sm text-red-600">{errors.contactEmail.message}</p>
                      )}
                    </div>

                    {/* Contact Phone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Phone Number
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                          type="tel"
                          {...register('contactPhone', validationRules.contactPhone)}
                          className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="+60123456789"
                        />
                      </div>
                      {errors.contactPhone && (
                        <p className="mt-1 text-sm text-red-600">{errors.contactPhone.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Additional Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Notes
                    </label>
                    <textarea
                      {...register('additionalNotes', validationRules.additionalNotes)}
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Any additional relevant information about the bidder or their bid..."
                    />
                    {errors.additionalNotes && (
                      <p className="mt-1 text-sm text-red-600">{errors.additionalNotes.message}</p>
                    )}
                  </div>

                  {/* Form Actions */}
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setShowAddForm(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                    >
                      <Save className="w-4 h-4 mr-2 inline" />
                      Add Bidder
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Bidders List */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                Off-Chain Bidders ({bidders.length})
              </h3>
              <div className="text-sm text-gray-500">
                Verified: {bidders.filter(b => b.kycStatus === 'Verified').length} | 
                Total Bid Amount: {formatCurrency(bidders.reduce((sum, b) => sum + b.bidAmount, 0))}
              </div>
            </div>

            {bidders.length === 0 ? (
              <div className="text-center py-12">
                <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-lg font-medium text-gray-900 mb-2">No bidders added yet</p>
                <p className="text-sm text-gray-600 mb-4">
                  Start by adding off-chain bidder information to this subscription period
                </p>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Bidder
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bidder Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type & Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Wallet Address
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bid Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Added
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {bidders.map((bidder) => (
                      <tr key={bidder.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {bidder.bidderType === 'Individual' ? (
                              <User className="h-5 w-5 text-gray-400 mr-3" />
                            ) : (
                              <Building2 className="h-5 w-5 text-gray-400 mr-3" />
                            )}
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {bidder.bidderName}
                              </div>
                              {bidder.additionalNotes && (
                                <div className="text-xs text-gray-500 truncate max-w-xs">
                                  {bidder.additionalNotes}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-xs text-gray-500 mb-1">{bidder.bidderType}</div>
                          {getKycStatusBadge(bidder.kycStatus)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-xs font-mono text-gray-600">
                            {`${bidder.walletAddress.slice(0, 6)}...${bidder.walletAddress.slice(-4)}`}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatCurrency(bidder.bidAmount)}
                          </div>
                          <div className="text-xs text-gray-500">
                            @ {bidder.bidPrice.toFixed(4)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-xs text-gray-600">
                            <div className="flex items-center mb-1">
                              <Mail className="h-3 w-3 mr-1" />
                              {bidder.contactEmail}
                            </div>
                            {bidder.contactPhone && (
                              <div className="flex items-center">
                                <Phone className="h-3 w-3 mr-1" />
                                {bidder.contactPhone}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                          {formatDate(bidder.dateAdded)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => removeBidder(bidder.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BidderManagement; 
